import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { VoucherViewData } from 'src/models/Management.interface';
import { voucherValidationSchema } from 'src/utils/validationScheme.utils';
import { voucherInitialValues } from 'src/utils/initialValues.utils';
import {
  apiUrls,
  voucherDialogTitles,
  voucherDialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import { useGetBasicVoucherTypes } from 'src/services/voucherType.service';
import {
  StyledDialog,
  DialogHeaderBox,
  DialogTitleTypography,
  DialogSubtitleTypography,
  CloseIconBox,
  DialogContentBox,
  FormFieldRow,
  FormFieldColumn,
  DialogActionsBox,
  SubmitButton,
} from './VoucherFormDialog.styles';

interface VoucherFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  data?: VoucherViewData | null;
  mode: 'add' | 'edit';
}

const VoucherFormDialog: FC<VoucherFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  data,
  mode,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [loading, setLoading] = useState(false);
  const { basicVoucherTypes, fetchBasicVoucherTypes } =
    useGetBasicVoucherTypes();

  const voucherTypeOptions: Option[] = basicVoucherTypes.map((vt) => ({
    label: vt.name,
    value: vt.id,
  }));

  useEffect(() => {
    if (open) {
      fetchBasicVoucherTypes();
    }
  }, [open]);

  const formik = useFormik({
    initialValues:
      mode === 'edit' && data
        ? {
            voucherTypeId: data.voucherTypeId || '',
            payee: data.payee || '',
            description: data.description || '',
            billNumber: data.billNumber || '',
            billAmount: data.billAmount?.toString() || '',
          }
        : voucherInitialValues,
    validationSchema: voucherValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (loading) return;

      setLoading(true);
      try {
        if (mode === 'edit' && data) {
          const response = await axiosTokenInstance.put(apiUrls.EDIT_VOUCHER, {
            id: data.id,
            voucherTypeId: values.voucherTypeId,
            payee: values.payee,
            description: values.description,
            billNumber: values.billNumber,
            billAmount: Number(values.billAmount),
          });
          showSnackbar(
            response.data.message || 'Voucher updated successfully',
            'success'
          );
        } else {
          const response = await axiosTokenInstance.post(apiUrls.ADD_VOUCHER, {
            voucherTypeId: values.voucherTypeId,
            payee: values.payee,
            description: values.description,
            billNumber: values.billNumber,
            billAmount: Number(values.billAmount),
          });
          showSnackbar(
            response.data.message || 'Voucher added successfully',
            'success'
          );
          formik.resetForm();
        }
        onSuccess();
      } catch (error: unknown) {
        const errorMessage = await getApiErrorMessage(error);
        showSnackbar(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleBillAmountChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      formik.setFieldValue('billAmount', raw);
    }
  };

  const formatBillAmount = (val: string) => {
    if (!val) return '';
    const [intPart, decPart] = val.split('.');
    const formattedInt = Number(intPart).toLocaleString('en-US');
    return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
  };

  useEffect(() => {
    if (open && mode === 'add') {
      formik.resetForm();
    }
  }, [open, mode]);

  const title =
    mode === 'edit' ? voucherDialogTitles.EDIT : voucherDialogTitles.ADD;
  const subtitle =
    mode === 'edit' ? voucherDialogSubtitles.EDIT : voucherDialogSubtitles.ADD;

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogHeaderBox>
        <DialogTitleTypography>{title}</DialogTitleTypography>
        <DialogSubtitleTypography>{subtitle}</DialogSubtitleTypography>
        <CloseIconBox
          component="i"
          className="bx bx-x-circle"
          onClick={handleClose}
        />
      </DialogHeaderBox>

      <DialogContentBox>
        <FormFieldRow>
          <FormFieldColumn>
            <TextField
              id="payee"
              label="Payee"
              formikProps={formik}
              width="100%"
              color="var(--subtext)"
            />
          </FormFieldColumn>
          <FormFieldColumn>
            <FormikSelectField
              id="voucherTypeId"
              label="Voucher Type"
              formikProps={formik}
              options={voucherTypeOptions}
              width="100%"
            />
          </FormFieldColumn>
        </FormFieldRow>

        <TextArea
          id="description"
          label="Description"
          formikProps={formik}
          rows={4}
          height="120px"
          width="100%"
          color="var(--subtext)"
        />

        <FormFieldRow>
          <FormFieldColumn>
            <TextField
              id="billNumber"
              label="Bill Number"
              formikProps={formik}
              width="100%"
              color="var(--subtext)"
            />
          </FormFieldColumn>
          <FormFieldColumn>
            <TextField
              id="billAmount"
              label="Bill Amount"
              formikProps={formik}
              width="100%"
              type="text"
              color="var(--subtext)"
              value={formatBillAmount(formik.values.billAmount)}
              onChange={handleBillAmountChange}
            />
          </FormFieldColumn>
        </FormFieldRow>
      </DialogContentBox>

      <DialogActionsBox>
        <SubmitButton
          onClick={() => formik.handleSubmit()}
          disabled={loading || formik.isSubmitting}
        >
          {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
        </SubmitButton>
      </DialogActionsBox>
    </StyledDialog>
  );
};

export default VoucherFormDialog;
